import { getConfig } from ".";

/**
 * Region routing for the multi-region CSM deployment.
 *
 * ONE global Static Web App fronts SEVERAL regional back ends — each region is its own
 * Function App + database. A user's data lives only in their home region's DB, so the
 * SWA must target the correct region's Function App (BFF) at runtime before any data
 * call. Selection is by region id -> `bffRoots[region]` (see regionRuntime.ts), which
 * defaults to the user's home region and can be switched for CORP impersonation — no
 * page navigation, no second SWA.
 *
 * ── The single source of truth is ORG_REGION below (org/WHOTO code -> region). ──
 * It is used for BOTH:
 *   1. Auto-routing regular users. The pre-BFF signal is the Microsoft Graph
 *      `companyName` from /me (global to Entra ID). PrivateRoute enforces
 *      `companyName === whoto` for a standard (non-corp) user, so a regular user's
 *      companyName IS their org code — it resolves through ORG_REGION directly.
 *   2. The CORP org switcher (OrgChangePage), which picks a target org by code.
 */

export type RegionId = string;

/**
 * Corp-capable companies (Graph `companyName`), mirrors the allow-list in PrivateRoute and
 * the backend's SupportBootstrapPolicy.SupportCompanies. Their companyName (e.g. "swagelok
 * manufacturing") is NOT an org code, so it is intentionally absent from ORG_REGION:
 * these users roam across regions via the switcher and must never be auto-routed
 * (that would trap them out of the region they're supporting).
 *
 * Not all of them are Swagelok — "k2 venture partners llc" is a third-party vendor engaged
 * to work on CSM (added 2026-09-18). It changes nothing about the rule above: a vendor has
 * no home region either, and membership of the environment's Entra access group is what
 * actually admits them.
 */
export const CORP_CAPABLE_COMPANIES = [
  "swagelok manufacturing",
  "swagelok annuity",
  "isle of man",
  "k2 venture partners llc",
];

/**
 * Org (WHOTO) code -> region whose database hosts that org's data.
 *
 *  - CORP / CORP2 / GEORG / VENTR  -> AMER: all live in the AMERICA database.
 *    Switching between them is in-DB RLS impersonation (no region hop).
 *  - PJAYA                         -> APAC: lives in the ASIA database.
 *  - LYON                          -> EMEA: lives in the EUROPE database.
 *    APAC and EMEA are each a separate deployment (own Function App + DB, and their own
 *    production SWA built from mainAsia / mainEmea), so switching to one of their orgs is
 *    a cross-region hop rather than in-DB impersonation.
 *
 * Add a row when an org onboards to a region. A region id is only ever as good as the
 * matching key in `bffRoots` — see bffRootForRegion, and note that EVERY environment's
 * BffRoots secret needs an entry for EVERY region a CORP user may switch into, not just
 * the region that environment is deployed in.
 */
const ORG_REGION: Record<string, RegionId> = {
  CORP: "AMER",
  CORP2: "AMER",
  GEORG: "AMER",
  VENTR: "AMER",
  PJAYA: "APAC",
  LYON: "EMEA",
};

/** Canonical form for an org/WHOTO code lookup (trim + upper-case). */
function canonicalOrg(value?: string): string {
  return value?.trim().toUpperCase() ?? "";
}

export function normalizeCompany(companyName?: string): string {
  return companyName?.trim().toLowerCase() ?? "";
}

export function isCorpCapableCompany(companyName?: string): boolean {
  return CORP_CAPABLE_COMPANIES.includes(normalizeCompany(companyName));
}

/** Region whose DB hosts the given org (WHOTO) code, or undefined if unknown. */
export function regionOfOrg(org?: string): RegionId | undefined {
  return ORG_REGION[canonicalOrg(org)];
}

/**
 * Home region of a user from their Graph companyName. For a regular (non-corp)
 * user companyName === their WHOTO code, so it resolves through the same ORG_REGION
 * map. Returns undefined for unmapped or corp-capable companies.
 */
export function resolveHomeRegion(companyName?: string): RegionId | undefined {
  return regionOfOrg(companyName);
}

/**
 * Region -> Function App (BFF) base URL map from config. Single SWA, multiple FAs:
 * the SWA is one global static app and picks which region's Function App to call at
 * runtime. Tolerates `bffRoots` arriving as a parsed object (config.aspx path) or a
 * JSON string (SWA token-injected fallback path — the authoritative one on Azure SWA).
 */
function bffRootMap(): Record<string, string> {
  const raw = getConfig()?.bffRoots;
  if (!raw) return {};
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw) as Record<string, string>;
    } catch {
      return {}; // unreplaced token (e.g. local dev) or malformed — fall back to config.bffRoot
    }
  }
  return raw;
}

/**
 * Function App base URL for a region, or undefined if unknown. The trailing slash is
 * preserved (bffRoot convention is `https://…/`, endpoints append `api/…`).
 */
export function bffRootForRegion(region: RegionId): string | undefined {
  return bffRootMap()[region] || undefined;
}
